import { memo, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { SUPPORT_EMAIL } from "@/config/supportContact";
import { IOS_APP_STORE_URL } from "@/mobile/appStoreUrl";

/**
 * Blocking "Update required" screen for the iOS shell (SD-19983).
 *
 * `App` renders this INSTEAD of the router once the minimum-build gate
 * reports, so there is nothing left to navigate to: no overlay, no z-index, no
 * focus trap to maintain.
 *
 * There is deliberately no "check again" control. The gate behind this screen
 * re-reads the policy on every foreground and on a standing timer, so the
 * screen already removes itself the moment it should — a button could only ask
 * the same question sooner, and its likeliest outcome is a visible no-op on a
 * screen that must not look broken. The copy carries that instead: it says
 * what ends this, so coming back to an unchanged screen reads as "not yet"
 * rather than "stuck".
 *
 * Hand-built rather than composed from `Empty`: that primitive is a
 * within-page empty state (dashed border, `flex-1`, page padding) and this is a
 * full-viewport app state, which is the `min-h-dvh` `<main>` pattern the public
 * quote and customer portal pages already use. A real `<h1>` also matters here
 * because this screen replaces the whole document, so the heading is the only
 * landmark a screen reader has. No icon, per DESIGN.md: a short muted sentence
 * and an action, no illustrations.
 *
 * The App Store link is a plain `<a>` on purpose. In the shell, a top-level
 * navigation away from the app origin is cancelled and handed to
 * `UIApplication.open` (Capacitor's `WebViewDelegationHandler`), and iOS routes
 * `apps.apple.com` to the App Store app as a universal link: real native
 * navigation, no WebView hijack and no plugin to add. `mailto:` leaves through
 * the same path.
 */
/**
 * Neither number is shown. A build means nothing to an operator, and iOS shows
 * them a version rather than a build anyway. They ride along in the support
 * email instead, where together they are the difference between "this device
 * is genuinely stale" and "we published a bad minimum" — the question support
 * is asked during exactly the incident this screen exists for.
 */
export interface NativeUpdateRequiredProps {
  installedBuild: number;
  minimumBuild: number;
}

const supportMailto = (installedBuild: number, minimumBuild: number): string =>
  `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    `Sameday iOS update required (build ${installedBuild}, minimum ${minimumBuild})`,
  )}`;

export const NativeUpdateRequired = memo(
  ({ installedBuild, minimumBuild }: NativeUpdateRequiredProps) => {
    const headingRef = useRef<HTMLHeadingElement>(null);

    useEffect(() => {
      // The router subtree was just unmounted, which drops focus to <body> and
      // announces nothing. Move it to the heading so assistive technology
      // reads the new context instead of going silent.
      headingRef.current?.focus();
    }, []);

    return (
      <main className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-background pt-[max(1.5rem,env(safe-area-inset-top))] pr-[max(1.5rem,env(safe-area-inset-right))] pb-[max(1.5rem,env(safe-area-inset-bottom))] pl-[max(1.5rem,env(safe-area-inset-left))] text-center text-foreground">
        <div className="flex max-w-sm flex-col gap-3">
          <h1
            ref={headingRef}
            tabIndex={-1}
            aria-describedby="native-update-required-description"
            // The focus above is programmatic and the heading is not reachable
            // by keyboard, so the browser's default ring would read as a stray
            // blue box around the title with nothing to indicate. The App
            // Store button keeps its own focus-visible ring.
            className="text-balance text-2xl font-semibold tracking-tight outline-none"
          >
            Update required
          </h1>
          <p
            id="native-update-required-description"
            className="text-pretty text-sm/relaxed text-muted-foreground"
          >
            This device is running a version of Sameday that is no longer
            supported. Nothing you have saved is lost, and Sameday picks up
            where you left off as soon as the update is installed.
          </p>
        </div>
        <Button asChild size="lg" className="h-11 w-full max-w-xs">
          {/* 44px, not the design system's 40px `lg`: this is the only control
              on a screen operated one-handed on a phone, where Apple's minimum
              target is 44pt. The native tab bar sets the same precedent. */}
          <a href={IOS_APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            Update in the App Store
          </a>
        </Button>
        {/* Not every blocked device can take the update: an older iOS than the
            new build supports, a managed device that blocks installs, a full
            disk. Without this they would have one button that can never work
            and no way to reach anyone. */}
        <p className="text-xs text-muted-foreground">
          Trouble updating?{" "}
          <a
            className="font-medium underline underline-offset-4"
            href={supportMailto(installedBuild, minimumBuild)}
          >
            Email support
          </a>
        </p>
      </main>
    );
  },
);
